import { ENDPOINTS } from '@/lib/consts'
import type { ChatProvider } from '@/composables/chat/types'
import type { AgentforceSessionData, SalesforceInAppChatConfigForEnv } from '@/composables/chat/providers/agentforce/types'
import {
  assertAgentforceSessionFromApi,
  assertValidAgentforcePayload,
  normalizeAgentforceJwtPayload,
} from '@/composables/chat/providers/agentforce/schemas'
import {
  setAgentforceChatBootstrapDeps,
  useAgentforce,
} from '@/composables/chat/providers/agentforce'

type ApiCall = (...args: unknown[]) => Promise<unknown>

const SALESFORCE_IN_APP_CHAT_VALUE_END_USER_LANGUAGE = 'endUserLanguage'
const SALESFORCE_IN_APP_CHAT_VALUE_DEVICE_TYPE = 'deviceType'
const SALESFORCE_IN_APP_CHAT_VALUE_APP_COUNTRY = 'appCountry'
const SALESFORCE_IN_APP_CHAT_VALUE_BUSINESS_PROVIDER_ID = 'businessProviderId'
const SALESFORCE_IN_APP_CHAT_VALUE_USER_ID = 'userId'
const SALESFORCE_IN_APP_CHAT_VALUE_APP_VERSION = 'appVersion'
const SALESFORCE_IN_APP_CHAT_VALUE_CURRENT_DATE_TIME = 'currentDateTime'

/** Pre-chat field key names for Salesforce ESW; embed URLs and org data come from core JWT only. */
export function getSalesforceInAppChatConfigForEnv (): SalesforceInAppChatConfigForEnv {
  return {
    prechatFieldKeys: {
      endUserLanguage: SALESFORCE_IN_APP_CHAT_VALUE_END_USER_LANGUAGE,
      deviceType: SALESFORCE_IN_APP_CHAT_VALUE_DEVICE_TYPE,
      appCountry: SALESFORCE_IN_APP_CHAT_VALUE_APP_COUNTRY,
      businessProviderId: SALESFORCE_IN_APP_CHAT_VALUE_BUSINESS_PROVIDER_ID,
      userId: SALESFORCE_IN_APP_CHAT_VALUE_USER_ID,
      appVersion: SALESFORCE_IN_APP_CHAT_VALUE_APP_VERSION,
      currentDateTime: SALESFORCE_IN_APP_CHAT_VALUE_CURRENT_DATE_TIME,
    },
  }
}

export { normalizeAgentforceJwtPayload }

/** Trims optional string fields on the session object (does not derive site URL from `scrtBaseUrl`). */
export function applyAgentforceEmbedDefaults (data: AgentforceSessionData): AgentforceSessionData {
  const embeddedSite = data.embedded_messaging_site_url?.trim() || undefined

  return {
    ...data,
    salesforce_origin: data.salesforce_origin?.trim() || undefined,
    agent_label: data.agent_label?.trim() || undefined,
    embedded_messaging_site_url: embeddedSite,
    embedded_messaging_deployment_name:
      data.embedded_messaging_deployment_name?.trim() || undefined,
  }
}

/**
 * POSTs to core `salesforce_in_app_chat_jwt`, normalizes and validates the payload, applies embed defaults.
 * Use {@link AgentforceSessionData.jwt} when you only need the token string.
 */
export async function getJwt (params: {
  apiCall: ApiCall
  businessId: number
}): Promise<AgentforceSessionData> {
  const res = (await params.apiCall({
    method: 'POST',
    url: ENDPOINTS.SALESFORCE_IN_CHAT_APP_JWT,
    data: {
      business_id: params.businessId,
    },
  })) as { data: unknown }

  const normalized = normalizeAgentforceJwtPayload(res.data)
  assertValidAgentforcePayload(normalized)
  const merged = applyAgentforceEmbedDefaults(normalized)
  assertAgentforceSessionFromApi(merged)
  return merged
}

/**
 * Registers Agentforce chat without calling the JWT API. The POST runs on first {@link useAgentforce} `open`.
 */
export async function setupAgentforceChat (params: {
  apiCall: ApiCall
  businessId: number
}): Promise<ChatProvider> {
  setAgentforceChatBootstrapDeps({ apiCall: params.apiCall, businessId: params.businessId })
  return useAgentforce()
}
